import { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Tooltip } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from '../../../redux/store';
import { ACTIVE_TICKETS } from '../../../reusable-components/datagrid/ticketColumns';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../reusable-components/snackbar';
import CustomToolbar from './CustomToolbar';
import { postMarkTicketAsNeedsResponse, getTicketSubtickets, postSetTicketSubticketsMessagesRead } from '../../../api/tickets';
import { getActiveTickets, updateSingleTicket, markAllAsReadOrUnread } from '../../../redux/slices/tickets';
import { getUnreadData } from '../../../redux/slices/dashboard';
import { useScopeCheck } from '../../../reusable-components/scopes';
import { useTabs } from '../../../context/TabContext';
import ManagerChipsWrapper from '../../../reusable-components/datagrid/ManagerChipsWrapper';
import { useGridScrollRestore } from '../../../hooks/useGridScrollRestore';
import Iconify from '../../../reusable-components/iconify';
import UrgentToggle from '../../../reusable-components/datagrid/UrgentToggle';
import PinToggle from '../../../reusable-components/datagrid/PinToggle';
import TicketQuickSetRemindMe from './ticket/TicketQuickSetRemindMe';
import { markMetricRemoved, markMetricAdded, NotificationMetricType } from '../../../redux/slices/notifications';
import { NotificationIdsChip, useNotificationIdsFilter } from '../../../hooks/useNotificationIdsFilter';

export default function OpenTickets() {
  const isFacilityManager = useScopeCheck(['Facility-Manager']);

  const navigate = useNavigate();
  const { matchesNotificationIds, isNotificationLoading } = useNotificationIdsFilter([NotificationMetricType.TicketsUnread]);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { addNewTabAndNavigate } = useTabs();
  const [showSelection, setShowSelection] = useState('');
  const [selected, setSelected] = useState([]);
  const [isTicketsLoaded, setIsTicketsLoaded] = useState(false);
  const apiRef = useGridApiRef();
  const gridId = 'open-tickets';

  useEffect(() => {
    dispatch(getActiveTickets()).then(() => setIsTicketsLoaded(true));
  }, [dispatch]);

  const { saveScrollAnchor } = useGridScrollRestore({ gridId, apiRef, ready: isTicketsLoaded });

  const urgent = {
    field: 'respondedTo',
    headerName: 'Urgent',
    cellClassName: () => 'no-x-padding',
    headerClassName: () => 'no-x-padding no-resizable',
    renderHeader: () => (
      <>
        <Tooltip title="No manager activity in 24 hours" placement="top" arrow>
          <Iconify icon="fa-solid:exclamation" width={12} height={12} />
        </Tooltip>
      </>
    ),
    minWidth: 15,
    headerAlign: 'center',
    maxWidth: 15,
    alignItems: 'left',
    disableReorder: true,
    renderCell: (params) => <UrgentToggle value={params.value} />,
  };

  const actions = {
    field: 'aboveLine',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) => (
      <>
        <PinToggle value={params.row.aboveLine} ticketId={params.row.id} />
        <TicketQuickSetRemindMe
          params={params}
          unpinTicket={(date) => dispatch(updateSingleTicket({ ...params.row, boomerangOn: date, aboveLine: false }))}
          boomerangOnColor="info"
          boomerangOffColor="secondary"
        />
      </>
    ),
    type: 'boolean',
    filterable: false,
    sortable: false,
    disableReorder: true,
  };

  const manager = ManagerChipsWrapper(() => dispatch(getActiveTickets()), 'Tickets', null, false, {
    onCellClick: (params) => params.event.stopPropagation(),
  }, false);

  const ticketCols = useMemo(
    () =>
      isFacilityManager
        ? [
            ACTIVE_TICKETS[0],
            urgent,
            ...ACTIVE_TICKETS.slice(1, 7),
            manager,
            ...ACTIVE_TICKETS.slice(7),
            actions,
          ]
        : [...ACTIVE_TICKETS.slice(0, 7), manager, ...ACTIVE_TICKETS.slice(7), actions],
    [isFacilityManager]
  );

  const {
    data: { activeTickets },
  } = useSelector((state) => state.tickets);

  const tickets = useMemo(
    () => (activeTickets || []).filter((ticket) => matchesNotificationIds(ticket.id, ...(ticket.subticketsUnread ?? []))),
    [activeTickets, matchesNotificationIds]
  );

  const handleMarkAsNeededResponse = async (ticketId, action) => {
    try {
      const { data } = await postMarkTicketAsNeedsResponse({ ticketId });
      if (data) {
        dispatch(updateSingleTicket(data));
      }
    } catch (error) {
      enqueueSnackbar(`Error: Could not ${action} label to ticket ${ticketId}`, {
        variant: 'error',
      });
    }
  };
  const handleMarkAsRead = async (ticketId, action) => {
    try {
      const response = await postSetTicketSubticketsMessagesRead({ id: ticketId, markAsUnread: action });
      if (response?.status === 200) {
        let subticketsUnread = [];
        if(action) {
          const ticketSubtickets = await getTicketSubtickets(ticketId);
          subticketsUnread = ticketSubtickets?.data || [];
        }
        dispatch(
          updateSingleTicket({
            id: ticketId,
            messageCount: action ? 1 : 0,
            subticketsUnread,
          })
        );
        dispatch(getUnreadData());
        if (action) {
          dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, ticketId));
        } else {
          dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ticketId));
        }
      }
    } catch (error) {
      enqueueSnackbar(`Error: Could not mark ticket ${ticketId} as ${action ? 'unread' : 'read'}`, {
        variant: 'error',
      });
    }
  };

  const customToolbar = useCallback(
    () => (
      <CustomToolbar
        setShowSelection={setShowSelection}
        showSelection={showSelection}
        selected={selected}
        markAllAsRead={markAllAsRead}
      />
    ),
    [selected, showSelection]
  );

  const rowClick = (id) => {
    saveScrollAnchor();
    navigate(`/dashboard/tickets/${id}`);
  };

  const markAllAsRead = async (markAsUnread, tickets) => {
    const ticketIds = [...tickets]
      .filter((ticket) => (markAsUnread ? !ticket.messageCount : ticket.messageCount))
      .map((ticket) => ticket.id);
    if (ticketIds.length > 0) {
      dispatch(markAllAsReadOrUnread(ticketIds, markAsUnread));
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            width: 'calc(100% - 10px)',
            height: 'calc(100vh - 205px)',
          }}
        >
          <>
            <NotificationIdsChip label="tickets" />
            <Box id="open-tickets-grid" sx={{ height: 'calc(100vh - 180px)' }}>
              <CustomDataGrid
                gridId={gridId}
                apiRef={apiRef}
                data={tickets}
                gridColumns={ticketCols}
                applyQuickFilter
                advancedFilter
                advancedSearchBase="tickets"
                quickfilterHiddenSearchCol={{ field: "lastMessage" }}
                CustomLeftToolbar={customToolbar}
                isLoading={!isTicketsLoaded || isNotificationLoading}
                checkboxSelection={!!showSelection}
                rowSelectionModel={selected}
                onRowSelectionModelChange={setSelected}
                defaultHiddenColumns={["createdBy", "createdOn"]}
                pinnedColumns={{
                  left: ['label', 'respondedTo', 'lastSentOn'],
                  right: ['aboveLine']
                }}
                persistentSort={[{ field: 'aboveLine', sort: 'desc' }]}
                                onRowClick={(params, e) => {
                  const selection = window.getSelection().toString();
                  if (selection.length > 0) return;
                  if(e.addChip) return; // prevent from going to ticket when clicking add manager chip, handle in ManagerChips component

                  if (e.navigateToOrders) {
                    addNewTabAndNavigate(`/dashboard/orders/${e.navigateToOrders}`);
                    return;
                  }

                  if (e.unread || e.read) {
                    handleMarkAsRead(params.id, !e.read);
                  } else if (e.urgent) {
                    handleMarkAsNeededResponse(params.id);
                  } else {
                    rowClick(params.row.id);
                  }
                }}
              />
            </Box>
          </>
        </Box>
      </Grid>
    </>
  );
}
